import React, { useEffect, useState, useCallback } from 'react';
import { useApi } from '../../hooks/useApi';
import { useClub } from '../../hooks/useClub';
import PB101OutcomeModal from './PB101OutcomeModal';

// ─── Script key lookup by section ────────────────────────────────────────────
function scriptCueForSection(section, record) {
  const phase = record.taskPhase || section;

  if (phase === 'session' || section === 'today') {
    // pb101_postsession_ask was deactivated 4/30/26 in favor of these two
    // more specific variants (matches the selection logic already used in
    // PB101RecordDetail.jsx / PB101OutcomeModal.jsx) — this branch pointed at
    // the retired script until fixed 9/17/26, so the Session Day card here
    // was silently missing a script (inactive scripts aren't served by
    // GET /scripts).
    if (record.stayedForPlay === true || record.stayedForPlay === 'true') {
      return { key: 'pb101_trial_asked_stayed', label: 'Script #5a — Trial Ask (Stayed)', note: 'Stayed for Open Play — strike now' };
    }
    return { key: 'pb101_trial_ask_notstayed', label: 'Script #5b — Trial Ask (Didn\'t Stay)', note: 'Softer ask — didn\'t stay for Open Play' };
  }
  if (phase === 'dayBefore' || phase === 'morningOf') {
    const iso = record.sessionDateRaw || '';
    const sessionDow = iso ? new Date(iso + 'T12:00:00').getDay() : null;
    const sessionLabel = sessionDow === 6 ? 'Sat 10:00 AM' : sessionDow === 3 ? 'Wed 6:00 PM' : 'session tomorrow';
    const notePrefix = phase === 'morningOf' ? 'Late registration · ' : 'Confirm attendance · ';
    return { key: 'pb101_op_followup_24hr', label: 'Script #3 — Day Before', note: `${notePrefix}${sessionLabel}` };
  }
  if (phase === 'confirmed') {
    return { key: 'pb101_op_morning_of', label: 'Script #4 — Session Reminder', note: 'OP reserved ✅ · remind about PB101 class' };
  }
  if (phase === 'opNudge') {
    return { key: 'pb101_op_invite_registration', label: 'Script #2b — OP Nudge', note: 'Invited · not yet reserved · gentle follow-up' };
  }
  if (phase === 'presend' || section === 'presend') {
    const diff = record.sessionDateRaw
      ? Math.round((new Date(record.sessionDateRaw + 'T12:00:00') - new Date()) / 86400000)
      : null;
    if (diff !== null && diff >= 5) {
      return { key: 'pb101_presession_5day', label: 'Script #1 — Pre-Session', note: 'Send once · 5+ days out' };
    }
    return { key: 'pb101_op_invite_registration', label: 'Script #2 — REGISTER + OPEN PLAY INVITE', note: `${diff !== null ? diff : '?'}D OUT · Send once` };
  }
  if (section === 'followup') {
    const attempt = record.followupCount || 0;
    if (attempt === 0) return { key: 'pb101_followup_1', label: 'Script #6 — Post-Session Follow-Up', note: '1st attempt · call or text' };
    return { key: 'pb101_followup_2', label: 'Script #7 — Re-Offer', note: '2nd attempt · last chance' };
  }
  if (section === 'noShow') {
    // Recent vs. stale tone (BK, 9/16/26) — same 6-week cutover the Win-Back
    // bucket already uses, so someone contacted right after their missed
    // session doesn't get language implying it just happened weeks later.
    const daysPast = record.sessionDateRaw
      ? Math.round((new Date() - new Date(record.sessionDateRaw + 'T12:00:00')) / 86400000)
      : null;
    if (daysPast !== null && daysPast > 42) {
      return { key: 'pb101_no_show_reinvite_stale', label: 'No-Show — Re-Invite (a while back)', note: 'Reschedule ask, not a trial pitch' };
    }
    return { key: 'pb101_no_show_reinvite_recent', label: 'No-Show — Re-Invite', note: 'Reschedule ask, not a trial pitch' };
  }
  if (section === 'winBack') {
    return { key: 'pb101_op_reengage', label: 'Win-Back — Free Open Play', note: 'Attended once, hasn\'t been back — free Open Play hook' };
  }
  return null;
}

// ─── Contact method badge ─────────────────────────────────────────────────────
function ContactBadge({ section }) {
  const isCall = section === 'followup' || section === 'today';
  return (
    <span style={{
      background: isCall ? 'rgba(234,88,12,0.2)' : 'rgba(59,130,246,0.2)',
      color: isCall ? '#f97316' : '#60a5fa',
      border: `1px solid ${isCall ? '#f97316' : '#60a5fa'}`,
      fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 4,
      textTransform: 'uppercase', letterSpacing: 0.5,
    }}>
      {isCall ? '📞 CALL FIRST' : '💬 TEXT FIRST'}
    </span>
  );
}

// ─── Inline Script Panel ──────────────────────────────────────────────────────
function InlineScriptPanel({ scriptCue, scripts, prospectName, clubName }) {
  const [copied, setCopied] = useState(false);

  const script = scripts?.find(s => s.script_key === scriptCue.key) || null;
  const firstName = prospectName?.split(' ')[0] || '[Name]';
  const displayClub = clubName || 'Picklr';
  const openerLine = `Hey ${firstName}, this is ${displayClub}.`;

  function fillScript(text) {
    if (!text) return text;
    return text
      .replace(/\[Name\]/g, firstName)
      .replace(/\[Rep Name\] from The Picklr \[Club Name\]/g, displayClub)
      .replace(/\[Rep Name\]/g, displayClub)
      .replace(/The Picklr \[Club Name\]/g, displayClub)
      .replace(/\[Club Name\]/g, displayClub);
  }

  const filledBody = fillScript(script?.body);

  const fullText = filledBody
    ? `${openerLine}\n\n${filledBody}`
    : openerLine;

  function handleCopy() {
    navigator.clipboard.writeText(fullText).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <div style={{
      marginTop: 8,
      background: '#0d1117',
      border: '1px solid #facc1540',
      borderRadius: 6,
      overflow: 'hidden',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '6px 10px',
        background: '#facc1510',
        borderBottom: '1px solid #facc1520',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: '#facc15', textTransform: 'uppercase', letterSpacing: '0.05em', fontFamily: 'DM Sans, sans-serif' }}>
            📋 {scriptCue.label}
          </span>
          <span style={{ fontSize: 10, color: '#666', fontStyle: 'italic' }}>{scriptCue.note}</span>
        </div>
        <button
          onClick={handleCopy}
          style={{
            background: copied ? '#22c55e22' : '#facc1520',
            color: copied ? '#22c55e' : '#facc15',
            border: `1px solid ${copied ? '#22c55e' : '#facc1540'}`,
            borderRadius: 4, padding: '2px 8px',
            fontSize: 10, fontWeight: 700, cursor: 'pointer',
            fontFamily: 'DM Sans, sans-serif',
            transition: 'all 0.15s ease',
          }}
        >
          {copied ? '✓ Copied' : 'Copy'}
        </button>
      </div>

      {script?.why_context && (
        <div style={{ padding: '5px 10px', borderBottom: '1px solid #1a1a1a', fontSize: 11, color: '#60a5fa', fontStyle: 'italic', lineHeight: 1.4 }}>
          💡 {script.why_context}
        </div>
      )}

      <div style={{ padding: '6px 10px', borderBottom: filledBody ? '1px solid #1a1a1a' : 'none' }}>
        <div style={{ fontSize: 9, color: '#555', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 3 }}>Opener</div>
        <div style={{ fontSize: 12, color: '#e5e5e5', fontStyle: 'italic', lineHeight: 1.5 }}>"{openerLine}"</div>
      </div>

      {filledBody && (
        <div style={{ padding: '6px 10px' }}>
          <div style={{ fontSize: 9, color: '#555', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 3 }}>Script</div>
          <div style={{ fontSize: 12, color: '#ccc', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
            {filledBody}
          </div>
          {script.timing && (
            <div style={{ marginTop: 6, fontSize: 10, color: '#f97316', fontWeight: 600 }}>
              ⏱ {script.timing}
            </div>
          )}
        </div>
      )}

      {!script && (
        <div style={{ padding: '4px 10px 8px', fontSize: 11, color: '#555', fontStyle: 'italic' }}>
          Use opener above — script not yet seeded.
        </div>
      )}
    </div>
  );
}

// ─── Open Play Capacity Badge ─────────────────────────────────────────────────
function SpotsRemainingBadge({ capacity, reserved }) {
  const cap = capacity ?? 8;
  const res = reserved || 0;
  const remaining = Math.max(0, cap - res);
  const overBy = Math.max(0, res - cap);
  const atCap  = remaining === 0 && overBy === 0;
  const nearCap = !atCap && remaining <= 2;
  // 9/23/26 — this is a suggested capacity, not a hard limit (BK:
  // "overbooking can be a club decision" given how often cancellations
  // happen), so over-capacity gets a heads-up tone, never the red
  // "blocked/error" language it used to show.
  const color = overBy > 0 ? '#f97316' : atCap ? '#eab308' : nearCap ? '#f97316' : '#22c55e';
  const bg    = overBy > 0 ? 'rgba(249,115,22,0.12)' : atCap ? 'rgba(234,179,8,0.12)' : nearCap ? 'rgba(249,115,22,0.12)' : 'rgba(34,197,94,0.10)';
  const label = overBy > 0 ? `⚠ ${res}/${cap} — over suggested cap`
    : atCap ? `At suggested cap (${cap}/${cap})`
    : `${remaining} spot${remaining !== 1 ? 's' : ''} left`;
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 4,
      background: bg, color, border: `1px solid ${color}55`,
      letterSpacing: '0.03em',
    }}>
      {label}
    </span>
  );
}

// ─── Individual task card ─────────────────────────────────────────────────────
function PB101Card({ record, section, expanded, onToggle, onLog, onView, onTheyResponded, scripts, clubName }) {
  const [showScript, setShowScript] = useState(false);
  const scriptCue = scriptCueForSection(section, record);
  const attempt = record.followupCount || 0;
  const isLastAttempt = record.isLastAttempt;
  const isReOfferDue = section === 'followup' && attempt >= 1;
  const isHotLead = record.nextAction === 'Sales Follow-Up — HOT LEAD' && section === 'followup';

  const daysOut = record.sessionDateRaw
    ? Math.round((new Date(record.sessionDateRaw) - new Date()) / 86400000)
    : null;

  useEffect(() => {
    if (!expanded) setShowScript(false);
  }, [expanded]);

  const borderColor = isHotLead ? '#ef4444' : isLastAttempt ? 'var(--red)' : 'var(--border)';

  return (
    <div
      style={{
        background: isHotLead ? 'rgba(239,68,68,0.05)' : 'var(--surface-2)',
        border: `1px solid ${borderColor}`,
        borderRadius: 8, marginBottom: 8, overflow: 'hidden',
        cursor: 'pointer',
      }}
      onClick={onToggle}
    >
      {/* ── HOT LEAD banner ── */}
      {isHotLead && (
        <div style={{
          background: 'rgba(239,68,68,0.15)',
          borderBottom: '1px solid rgba(239,68,68,0.3)',
          padding: '4px 14px',
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <span style={{
            background: '#ef4444', color: '#fff',
            fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 3,
            letterSpacing: '0.05em',
          }}>🔥 HOT LEAD</span>
          <span style={{ fontSize: 10, color: '#ef4444', fontWeight: 600 }}>
            Sales Follow-Up Required · Contact within 4 hours
          </span>
        </div>
      )}

      {/* ── Collapsed row ── */}
      <div style={{ padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>

        {/* Left — name + meta */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 3 }}>{record.name}</div>

          {/* Script cue line */}
          {scriptCue && !isHotLead && (
            <div style={{ fontSize: 11, color: 'var(--yellow)', fontStyle: 'italic', marginBottom: 3 }}>
              → {scriptCue.label} · {scriptCue.note}
            </div>
          )}

          {isHotLead && (
            <div style={{ fontSize: 11, color: '#ef4444', fontStyle: 'italic', marginBottom: 3 }}>
              → F.Desk flagged as hot · Sales follow-up needed now
            </div>
          )}

          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            {record.sessionDate && (
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{record.sessionDate}</span>
            )}
            {record.skillLevel && (
              <span style={{ fontSize: 10, color: 'var(--text-muted)', background: 'var(--surface-3)', padding: '1px 6px', borderRadius: 4 }}>
                {record.skillLevel}
              </span>
            )}
            {(section === 'today' || section === 'presend') && record.openPlayInvited && (
              <SpotsRemainingBadge capacity={record.openPlayCapacity} reserved={record.openPlaySpotsReserved} />
            )}
            <ContactBadge section={section} />
          </div>
        </div>

        {/* Right — badges + LOG OUTCOME button */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 5 }} onClick={e => e.stopPropagation()}>

          {/* HOT LEAD urgency badge */}
          {isHotLead && (
            <span style={{
              background: '#ef444422', color: '#ef4444',
              border: '1px solid #ef4444',
              fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 4,
              textTransform: 'uppercase',
            }}>
              ⏱ WITHIN 4 HRS
            </span>
          )}

          {/* Attempt badge — only for non-hot-lead followups */}
          {section === 'followup' && !isHotLead && (
            <span style={{
              background: isLastAttempt ? 'var(--red)' : 'rgba(234,179,8,0.2)',
              color: isLastAttempt ? '#fff' : 'var(--yellow)',
              fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 4,
              textTransform: 'uppercase',
            }}>
              {isLastAttempt ? '⚠ LAST ATTEMPT' : `ATTEMPT ${attempt + 1} OF 2`}
            </span>
          )}

          {/* RE-OFFER DUE badge */}
          {isReOfferDue && !isLastAttempt && !isHotLead && (
            <span style={{
              background: 'rgba(234,88,12,0.2)', color: '#f97316',
              fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 4,
            }}>
              RE-OFFER DUE
            </span>
          )}

          {/* Days out badge for pre-send */}
          {section === 'presend' && daysOut !== null && (
            <span style={{
              background: 'rgba(59,130,246,0.2)', color: '#60a5fa',
              fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 4,
            }}>
              {daysOut}D OUT
            </span>
          )}

          {/* LOG OUTCOME button */}
          <button
            style={{
              background: isHotLead ? '#ef4444' : 'var(--yellow)',
              color: '#000',
              border: 'none', borderRadius: 5, padding: '4px 10px',
              fontSize: 11, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
            }}
            onClick={e => { e.stopPropagation(); onLog(); }}
          >
            LOG OUTCOME
          </button>
        </div>
      </div>

      {/* ── LAST ATTEMPT warning banner ── */}
      {isLastAttempt && !isHotLead && (
        <div style={{
          background: 'rgba(220,38,38,0.15)', borderTop: '1px solid var(--red)',
          padding: '6px 14px', fontSize: 11, color: 'var(--red)', fontWeight: 600,
        }}>
          ⚠ LAST ATTEMPT — AUTO-LOST IF NO RESPONSE
        </div>
      )}

      {/* ── Expanded detail ── */}
      {expanded && (
        <div style={{ borderTop: '1px solid var(--border)', padding: '12px 14px' }} onClick={e => e.stopPropagation()}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 20px', marginBottom: 12 }}>
            {record.phone      && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>📞 {record.phone}</span>}
            {record.email      && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>✉ {record.email}</span>}
            {record.leadSource && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Source: {record.leadSource}</span>}
            {record.trialAskResult && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Ask Result: {record.trialAskResult}</span>}
            {record.nextAction && <span style={{ fontSize: 12, color: isHotLead ? '#ef4444' : 'var(--text-muted)', gridColumn: 'span 2', fontWeight: isHotLead ? 700 : 400 }}>Next: {record.nextAction}</span>}
            {record.notes      && <span style={{ fontSize: 12, color: 'var(--text-muted)', gridColumn: 'span 2', fontStyle: 'italic' }}>{record.notes}</span>}
          </div>

          {/* Script panel toggle */}
          {scriptCue && (
            <div style={{ marginBottom: 10 }}>
              <button
                style={{
                  background: showScript ? '#facc1520' : 'none',
                  border: '1px solid var(--yellow)', color: 'var(--yellow)',
                  borderRadius: 5, padding: '4px 10px', fontSize: 11, cursor: 'pointer',
                }}
                onClick={() => setShowScript(p => !p)}
              >
                {showScript ? '▲ Hide Script' : '▼ View Script'}
              </button>
              {showScript && (
                <InlineScriptPanel
                  scriptCue={scriptCue}
                  scripts={scripts}
                  prospectName={record.name}
                  clubName={clubName}
                />
              )}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              style={{
                background: isHotLead ? '#ef4444' : 'var(--yellow)',
                color: '#000', border: 'none', borderRadius: 5, padding: '6px 14px',
                fontSize: 12, fontWeight: 700, cursor: 'pointer',
              }}
              onClick={onLog}
            >
              {isHotLead ? '🔥 Log Sales Contact' : 'Log Outcome'}
            </button>
            <button
              style={{ background: 'none', border: '1px solid var(--border)', color: 'var(--text)', borderRadius: 5, padding: '6px 14px', fontSize: 12, cursor: 'pointer' }}
              onClick={onView}
            >
              View Record
            </button>
            <button
              style={{ background: 'none', border: '1px solid var(--border)', color: 'var(--text-muted)', borderRadius: 5, padding: '6px 14px', fontSize: 12, cursor: 'pointer' }}
              onClick={onToggle}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function PB101Tasks({ onSelectRecord }) {
  const { get } = useApi();
  const { currentClub } = useClub();
  const [data, setData]             = useState(null);
  const [loading, setLoading]       = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [outcomeTarget, setOutcomeTarget] = useState(null);
  const [completed, setCompleted]   = useState([]);
  const [suppressed, setSuppressed] = useState([]);
  const [search, setSearch]         = useState('');
  const [scripts, setScripts]       = useState([]);

  const load = useCallback(() => {
    // GUARD (Sprint 19.9 bug fix): currentClub can be null on initial render
    // for users whose ClubContext doesn't auto-resolve a single default club
    // (e.g. mloc/admin roles with multiple or 'ALL' club access) — previously
    // this crashed the whole module with "Cannot read properties of null
    // (reading 'id')" and no error boundary caught it, showing a silent black
    // screen. Bail out cleanly instead; load() re-runs automatically once
    // currentClub resolves, via the dependency array below.
    if (!currentClub?.id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    get(`/pb101/tasks?club=${currentClub.id}`)
      .then(setData)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [currentClub?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    get('/scripts?module=pb101')
      .then(res => setScripts(res?.scripts || []))
      .catch(() => setScripts([]));
  }, [currentClub?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="empty-state"><div className="spinner" /></div>;
  if (!data)   return <div className="empty-state"><p>No task data.</p></div>;

  const completedIds  = new Set(completed.map(c => c.id));
  const suppressedIds = new Set(suppressed.map(s => s.id));
  const searchLower   = search.toLowerCase();
  const isSearching   = search.trim().length > 0;

  // ── Stage-based bucketing — driven by taskPhase from backend ───────────────
  const allPreSend = (data.preSend || []).filter(i => !completedIds.has(i.id) && !suppressedIds.has(i.id));
  const stage4_session_day = (data.sessionToday || []).filter(i => !completedIds.has(i.id) && !suppressedIds.has(i.id));

  const stage1_register   = allPreSend.filter(r => r.taskPhase === 'presend' && (() => {
    const d = r.sessionDateRaw ? Math.round((new Date(r.sessionDateRaw + 'T12:00:00') - new Date()) / 86400000) : null;
    return d === null || d >= 2;
  })());
  const stage2_op_invite  = allPreSend.filter(r => r.taskPhase === 'presend' && (() => {
    const d = r.sessionDateRaw ? Math.round((new Date(r.sessionDateRaw + 'T12:00:00') - new Date()) / 86400000) : null;
    return d !== null && d >= 2 && d <= 4;
  })());
  const stage2b_op_nudge  = allPreSend.filter(r => r.taskPhase === 'opNudge');
  const stage2c_confirmed = allPreSend.filter(r => r.taskPhase === 'confirmed');
  const stage3_day_before = allPreSend.filter(r => r.taskPhase === 'dayBefore' || r.taskPhase === 'morningOf');

  // Post-session: sales handoff
  const allFollowUp    = (data.followUp || []).filter(i => !completedIds.has(i.id) && !suppressedIds.has(i.id));
  const hotLeads       = allFollowUp.filter(r => r.trialAskResult === 'Sales Follow-Up Needed' || r.nextAction === 'Sales Follow-Up — HOT LEAD');
  const stage6_followup = allFollowUp.filter(r => r.trialAskResult !== 'Sales Follow-Up Needed' && r.nextAction !== 'Sales Follow-Up — HOT LEAD' && (r.followupCount || 0) === 0);
  const stage7_reoffer  = allFollowUp.filter(r => r.trialAskResult !== 'Sales Follow-Up Needed' && r.nextAction !== 'Sales Follow-Up — HOT LEAD' && (r.followupCount || 0) >= 1);
  const regularFollowUp = [...stage6_followup, ...stage7_reoffer];

  // No-Show / Win-Back (Sprint 21.2, per COMS_PB101_WinBack_Spec_v1) — never
  // age out (No-Show) or run 6wks-4mo out (Win-Back), but kept structurally
  // separate from this week's active work so a wide backfill can never crowd
  // out real, time-sensitive follow-ups. Gated server-side to a 3-attempt,
  // 7-day reinvite cadence — BK, 9/16/26.
  const stage8_noShow  = (data.noShow  || []).filter(i => !completedIds.has(i.id) && !suppressedIds.has(i.id));
  const stage9_winBack = (data.winBack || []).filter(i => !completedIds.has(i.id) && !suppressedIds.has(i.id));

  const sections = [
    { key: 'presend',   label: '📋 REGISTER + OPEN PLAY INVITE',       items: stage1_register,   owner: 'F.DESK', ownerColor: '#3b82f6',
      hint: 'Send Open Play invite immediately after registration' },
    { key: 'presend',   label: '💬 OPEN PLAY INVITE',                  items: stage2_op_invite,  owner: 'F.DESK', ownerColor: '#3b82f6',
      hint: '2-4 days out — invite to Open Play if not yet done' },
    { key: 'presend',   label: '🔔 OP INVITE SENT — AWAITING RESPONSE', items: stage2b_op_nudge,  owner: 'F.DESK', ownerColor: '#f59e0b',
      hint: 'Invited but not reserved — gentle nudge to grab their spot' },
    { key: 'confirmed', label: '✅ CONFIRMED — PB101 REMINDER',         items: stage2c_confirmed, owner: 'F.DESK', ownerColor: '#22c55e',
      hint: 'OP reserved ✅ — send gentle reminder about the PB101 class itself' },
    { key: 'presend',   label: '⏰ DAY BEFORE — CONFIRM ATTENDANCE',    items: stage3_day_before, owner: 'F.DESK', ownerColor: '#3b82f6',
      hint: 'Remind them — session is tomorrow. Stop outreach 2hrs before event.' },
    { key: 'today',     label: '🏓 SESSION DAY',                        items: stage4_session_day,owner: 'F.DESK + SALES', ownerColor: '#eab308',
      hint: 'Log attendance + make the trial ask today' },
    { key: 'followup',  label: '📞 POST-SESSION FOLLOW-UP',             items: stage6_followup,   owner: 'SALES',  ownerColor: '#22c55e',
      hint: 'First follow-up — soft re-offer, 2 days after session' },
    { key: 'followup',  label: '🔄 RE-OFFER — SECOND ATTEMPT',          items: stage7_reoffer,    owner: 'SALES',  ownerColor: '#22c55e',
      hint: 'Last chance — if no response after this, record auto-closes' },
    { key: 'noShow',  label: '⏳ NO-SHOW RE-INVITE',                    items: stage8_noShow,  owner: 'F.DESK', ownerColor: '#a855f7',
      hint: 'Never attended — never expires. Reschedule ask, not a trial pitch. Up to 3 tries, once a week.' },
    { key: 'winBack', label: '🔁 WIN-BACK — QUIET SINCE PB101',         items: stage9_winBack, owner: 'F.DESK', ownerColor: '#a855f7',
      hint: 'Attended but hasn\'t been back — free Open Play re-invite. Up to 3 tries, once a week.' },
  ];

  function allItems() {
    return sections.flatMap(s => s.items);
  }

  function matchesSearch(r) {
    return (
      r.name?.toLowerCase().includes(searchLower) ||
      r.email?.toLowerCase().includes(searchLower) ||
      r.phone?.includes(search)
    );
  }

  function handleToggle(id) {
    setExpandedId(prev => prev === id ? null : id);
  }

  function handleOutcomeSaved(record, outcome) {
    setOutcomeTarget(null);
    setExpandedId(null);
    if (outcome === 'Do Not Contact') {
      setSuppressed(prev => [...prev.filter(s => s.id !== record.id), { ...record, outcome }]);
    } else {
      setCompleted(prev => [...prev.filter(c => c.id !== record.id), { ...record, outcome }]);
    }
    load();
  }

  function handleTheyResponded(record) {
    setCompleted(prev => prev.filter(c => c.id !== record.id));
    setSuppressed(prev => prev.filter(s => s.id !== record.id));
    setOutcomeTarget(record);
  }

  const dueCount = sections.reduce((n, s) =>
    n + s.items.filter(i => !completedIds.has(i.id) && !suppressedIds.has(i.id)).length, 0
  );

  // Capacity aggregate — fixed 9/23/26 (GAP-P9 real enforcement build):
  // capacity/reserved are now real per-SESSION values from the backend
  // (openPlayCapacity/openPlaySpotsReserved), shared across every person
  // attending that same club+date's Open Play. Summing them per-record like
  // before multiplied capacity by however many people were in the cohort
  // (5 people => "40 spots") instead of reflecting the one real shared cap —
  // take one representative record per distinct session date instead.
  const todaySessions = (data.sessionToday || []).filter(r => r.openPlayInvited);
  const sessionsByDate = new Map();
  for (const r of todaySessions) {
    if (!sessionsByDate.has(r.sessionDateRaw)) sessionsByDate.set(r.sessionDateRaw, r);
  }
  const uniqueSessions = [...sessionsByDate.values()];
  const totalCapacity = uniqueSessions.reduce((n, r) => n + (r.openPlayCapacity ?? 8), 0);
  const totalReserved = uniqueSessions.reduce((n, r) => n + (r.openPlaySpotsReserved || 0), 0);
  const totalSpotsLeft = Math.max(0, totalCapacity - totalReserved);

  const cardProps = (record, sectionKey) => ({
    key: record.id,
    record,
    section: sectionKey,
    expanded: expandedId === record.id,
    onToggle: () => handleToggle(record.id),
    onLog: () => { setExpandedId(record.id); setOutcomeTarget(record); },
    onView: () => onSelectRecord(record.id),
    onTheyResponded: () => handleTheyResponded(record),
    scripts,
    clubName: currentClub?.name ? `Picklr ${currentClub.name}` : 'Picklr',
  });

  return (
    <div>
      {/* ── KPI pills ── */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {[
          { label: 'DUE TODAY',     value: dueCount,                          color: dueCount > 0 ? 'var(--yellow)' : 'var(--green)' },
          { label: 'SESSION DAY',   value: stage4_session_day.length },
          { label: 'PRE-SESSION',   value: stage1_register.length + stage2_op_invite.length + stage2b_op_nudge.length + stage3_day_before.length },
          { label: 'CONFIRMED',     value: stage2c_confirmed.length },
          { label: 'FOLLOW-UP',     value: stage6_followup.length + stage7_reoffer.length },
          { label: '🔥 HOT LEADS',  value: hotLeads.length,                   color: hotLeads.length > 0 ? '#ef4444' : 'var(--text-muted)' },
          ...(todaySessions.length > 0 ? [{ label: 'SPOTS LEFT', value: totalSpotsLeft, color: totalSpotsLeft === 0 ? '#ef4444' : totalSpotsLeft <= 2 ? '#f97316' : '#22c55e' }] : []),
          { label: 'COMPLETED',     value: completed.length,                  color: 'var(--green)' },
        ].map(k => (
          <div key={k.label} style={{
            background: 'var(--surface-2)', border: `1px solid ${k.label === 'HOT LEADS' && hotLeads.length > 0 ? '#ef444440' : 'var(--border)'}`,
            borderRadius: 7, padding: '6px 12px', textAlign: 'center', minWidth: 80,
          }}>
            <div style={{ fontSize: 9, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 2 }}>{k.label}</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: k.color || 'var(--text)', fontFamily: 'DM Mono, monospace' }}>{k.value}</div>
          </div>
        ))}
      </div>

      {/* ── Search ── */}
      <div style={{ marginBottom: 16 }}>
        <input
          className="field-input"
          type="text"
          placeholder="Search by name, email, phone..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ width: '100%' }}
        />
      </div>

      {/* ── Search results ── */}
      {isSearching && (
        <div>
          <div className="section-header">Search Results</div>
          {allItems()
            .filter(r => !completedIds.has(r.id) && !suppressedIds.has(r.id) && matchesSearch(r))
            .map(record => {
              const section = (data.sessionToday || []).find(i => i.id === record.id) ? 'today'
                : (data.followUp || []).find(i => i.id === record.id) ? 'followup'
                : (data.preSend || []).find(i => i.id === record.id) ? (record.taskPhase || 'presend') : 'presend';
              return <PB101Card {...cardProps(record, section)} />;
            })}
        </div>
      )}

      {/* ── Main sections ── */}
      {!isSearching && (
        <div>
          {dueCount === 0 && completed.length === 0 && suppressed.length === 0 && (
            <div className="empty-state">
              <div className="empty-state-icon">✅</div>
              <div className="empty-state-text">No tasks today.</div>
            </div>
          )}

          {/* ── HOT LEADS — always first ── */}
          {hotLeads.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <div style={{
                fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.5,
                color: '#ef4444', marginBottom: 8,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                paddingBottom: 6, borderBottom: '1px solid rgba(239,68,68,0.3)',
              }}>
                <span>🔥 Sales Follow-Up Required</span>
                <span style={{
                  background: '#ef4444', color: '#fff',
                  borderRadius: 10, padding: '1px 8px', fontSize: 10, fontWeight: 700,
                }}>{hotLeads.length}</span>
              </div>
              <div style={{ fontSize: 11, color: '#ef4444', marginBottom: 8, fontStyle: 'italic', opacity: 0.8 }}>
                Contact within 4 hours · F.Desk flagged these as hot
              </div>
              {hotLeads.map(record => <PB101Card {...cardProps(record, 'followup')} />)}
            </div>
          )}

          {/* ── Regular sections ── */}
          {sections.map(({ key, label, items, owner, ownerColor, hint }, sectionIdx) => {
            const active = items.filter(i => !completedIds.has(i.id) && !suppressedIds.has(i.id));
            if (active.length === 0) return null;

            // Show Sales handoff divider before first SALES section
            const prevOwner = sectionIdx > 0 ? sections[sectionIdx - 1].owner : null;
            const showHandoff = owner === 'SALES' && prevOwner !== 'SALES';

            return (
              <div key={`${key}-${sectionIdx}`}>
                {showHandoff && (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    margin: '20px 0 16px', 
                  }}>
                    <div style={{ flex: 1, height: 1, background: 'linear-gradient(to right, transparent, #22c55e44)' }} />
                    <span style={{
                      fontSize: 9, fontWeight: 800, color: '#22c55e',
                      background: '#22c55e11', border: '1px solid #22c55e33',
                      borderRadius: 4, padding: '3px 10px', letterSpacing: '0.1em',
                      textTransform: 'uppercase', whiteSpace: 'nowrap',
                    }}>↓ Sales Handoff</span>
                    <div style={{ flex: 1, height: 1, background: 'linear-gradient(to left, transparent, #22c55e44)' }} />
                  </div>
                )}
                <div style={{ marginBottom: 20 }}>
                  <div style={{
                    fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.5,
                    color: 'var(--text-muted)', marginBottom: hint ? 4 : 8,
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    paddingBottom: 6, borderBottom: `1px solid ${ownerColor}30`,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span>{label}</span>
                      <span style={{
                        background: ownerColor + '22',
                        color: ownerColor,
                        border: `1px solid ${ownerColor}55`,
                        borderRadius: 4, padding: '1px 6px',
                        fontSize: 9, fontWeight: 700, letterSpacing: '0.05em',
                      }}>{owner}</span>
                    </div>
                    <span style={{
                      background: 'var(--surface-3)', color: 'var(--text-muted)',
                      borderRadius: 10, padding: '1px 8px', fontSize: 10,
                    }}>{active.length}</span>
                  </div>
                  {hint && (
                    <div style={{ fontSize: 11, color: '#555', fontStyle: 'italic', marginBottom: 8 }}>
                      {hint}
                    </div>
                  )}
                  {active.map(record => <PB101Card {...cardProps(record, key)} />)}
                </div>
              </div>
            );
          })}

          {/* ── DO NOT CONTACT TODAY ── */}
          {suppressed.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <div style={{
                fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.5,
                color: 'var(--text-muted)', marginBottom: 8,
                display: 'flex', justifyContent: 'space-between',
              }}>
                <span>🚫 Do Not Contact Today</span>
                <span style={{ background: 'var(--surface-3)', color: 'var(--text-muted)', borderRadius: 10, padding: '1px 8px', fontSize: 10 }}>
                  {suppressed.length}
                </span>
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8, fontStyle: 'italic' }}>
                They Responded bypasses hold immediately
              </div>
              {suppressed.map(record => (
                <div key={record.id} style={{
                  background: 'var(--surface-2)', border: '1px solid var(--border)',
                  borderRadius: 8, padding: '10px 14px', marginBottom: 6,
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{record.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{record.sessionDate}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button
                      style={{ background: 'none', border: '1px solid var(--border)', color: 'var(--text-muted)', borderRadius: 5, padding: '4px 10px', fontSize: 11, cursor: 'pointer' }}
                      onClick={() => { setOutcomeTarget(record); setSuppressed(prev => prev.filter(s => s.id !== record.id)); }}
                    >
                      Contact Anyway
                    </button>
                    <button
                      style={{ background: 'var(--yellow)', color: '#000', border: 'none', borderRadius: 5, padding: '4px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
                      onClick={() => handleTheyResponded(record)}
                    >
                      ↩ They Responded
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ── COMPLETED TODAY ── */}
          {completed.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <div style={{
                fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.5,
                color: 'var(--text-muted)', marginBottom: 8,
                display: 'flex', justifyContent: 'space-between',
              }}>
                <span>✅ Completed Today</span>
                <span style={{ background: 'rgba(34,197,94,0.15)', color: 'var(--green)', borderRadius: 10, padding: '1px 8px', fontSize: 10, fontWeight: 700 }}>
                  {completed.length}
                </span>
              </div>
              {completed.map(record => (
                <div key={record.id} style={{
                  background: 'var(--surface-2)', border: '1px solid var(--border)',
                  borderRadius: 8, padding: '10px 14px', marginBottom: 6,
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  opacity: 0.75,
                }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{record.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{record.sessionDate}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <span style={{
                      background: 'rgba(34,197,94,0.15)', color: 'var(--green)',
                      fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
                    }}>
                      {record.outcome}
                    </span>
                    <button
                      style={{ background: 'var(--yellow)', color: '#000', border: 'none', borderRadius: 5, padding: '4px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
                      onClick={() => handleTheyResponded(record)}
                    >
                      ↩ They Responded
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {outcomeTarget && (
        <PB101OutcomeModal
          record={outcomeTarget}
          onClose={() => setOutcomeTarget(null)}
          onSaved={(outcome) => handleOutcomeSaved(outcomeTarget, outcome)}
        />
      )}
    </div>
  );
}
